/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_property_rights-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_property_rights-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_property_rights-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, sort_order, archived, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_property_rights-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_property_rights-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_property_rights.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_property_rights-entity-config.ts
 * Public fields: 8/8 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupPropertyRightsClient, LookupPropertyRightsQueryOptions } from '../generated_clients/generated-lookup_property_rights-api-client.js';
import {
  LookupPropertyRightsInsert,
  LookupPropertyRightsUpdate,
  LookupPropertyRightsFieldConfig
} from '../../types/generated_schemas/generated-lookup_property_rights-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_property_rights_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupPropertyRightsKeys = {
  all: ['lookup_property_rights'] as const,
  lists: () => [...lookupPropertyRightsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupPropertyRightsQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupPropertyRightsKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupPropertyRightsKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupPropertyRightsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupPropertyRightsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupPropertyRightsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupPropertyRightsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupPropertyRightsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupPropertyRightsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupPropertyRightsClient();
};

/**
 * Hook to get all lookup_property_rights with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_property_rights data
 */
export const useLookupPropertyRightsList = (options?: LookupPropertyRightsQueryOptions) => {
  const client = useLookupPropertyRightsClient();
  
  return useQuery({
    queryKey: lookupPropertyRightsKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_property_right by ID
 *
 * @param id - The lookup_property_right ID
 * @returns React Query result with single lookup_property_right data
 */
export const useLookupPropertyRights = (id: string) => {
  const client = useLookupPropertyRightsClient();
  
  return useQuery({
    queryKey: lookupPropertyRightsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_property_rights
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_property_rights_business_hooks/
 */

/**
 * Hook to create a new lookup_property_right
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, sort_order, archived, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_property_right
 */
export const useCreateLookupPropertyRights = () => {
  const client = useLookupPropertyRightsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupPropertyRightsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupPropertyRightsKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_property_right
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_property_right
 */
export const useUpdateLookupPropertyRights = () => {
  const client = useLookupPropertyRightsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupPropertyRightsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupPropertyRightsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupPropertyRightsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_property_right
 *
 * @returns React Query mutation for deleting lookup_property_right
 */
export const useDeleteLookupPropertyRights = () => {
  const client = useLookupPropertyRightsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupPropertyRightsKeys.all });
    },
  });
};

/**
 * Hook to count lookup_property_rights with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupPropertyRightsCount = (filter?: Partial<Record<typeof LookupPropertyRightsFieldConfig.filterable[number], any>>) => {
  const client = useLookupPropertyRightsClient();
  
  return useQuery({
    queryKey: lookupPropertyRightsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupPropertyRightsFieldConfig = () => {
  return LookupPropertyRightsFieldConfig;
};